import { Link } from "@inertiajs/react";
import { ArrowLeft } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";

interface LogEntry {
  id: number;
  action: "allowed" | "redirected" | "blocked" | "failed";
  recipient_category: "candidate" | "firm" | "system";
  original_to: string[];
  final_to: string[];
  subject: string;
  reason: string | null;
  caller_location: string | null;
  sent_at: string;
}

interface Props {
  logs: LogEntry[];
}

const ACTION_STYLES: Record<LogEntry["action"], string> = {
  allowed:    "bg-green-50 text-green-700 border-green-200",
  redirected: "bg-amber-50 text-amber-700 border-amber-200",
  blocked:    "bg-red-50 text-red-700 border-red-200",
  failed:     "bg-gray-100 text-gray-600 border-gray-200",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
  });
}

export default function EmailSafetyLog({ logs }: Props) {
  return (
    <AppLayout title="Email Safety Log">
      <div className="max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <Link
            href="/settings"
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft size={13} /> Settings
          </Link>
          <span className="text-gray-300">/</span>
          <h1 className="text-xl font-semibold text-gray-900">Email Safety Log</h1>
        </div>

        {logs.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg px-5 py-8 text-center text-sm text-gray-400">
            No email safety events recorded yet.
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-4">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Recent Events</span>
              <span className="text-xs text-gray-400">(last {logs.length})</span>
            </div>
            <div className="divide-y divide-gray-100">
              {logs.map((log) => (
                <div key={log.id} className="px-5 py-3 flex items-start gap-4">
                  <span className={`mt-0.5 shrink-0 text-[10px] font-semibold uppercase tracking-wide border rounded px-1.5 py-0.5 ${ACTION_STYLES[log.action]}`}>
                    {log.action}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-xs font-medium text-gray-800 truncate">{log.subject}</span>
                      <span className="text-[10px] text-gray-400 uppercase">{log.recipient_category}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      To: {log.original_to.join(", ")}
                      {log.action === "redirected" && log.final_to.length > 0 && (
                        <span className="text-amber-600"> → {log.final_to.join(", ")}</span>
                      )}
                    </p>
                    {log.reason && (
                      <p className="text-xs text-gray-400 mt-0.5 font-mono">{log.reason}</p>
                    )}
                    {log.caller_location && (
                      <p className="text-[10px] text-gray-300 mt-0.5 font-mono truncate">{log.caller_location}</p>
                    )}
                  </div>
                  <span className="shrink-0 text-[10px] text-gray-400">{formatDate(log.sent_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
